import { useEffect, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

function Stepper({ value, onChange, testId }) {
  const n = parseInt(value, 10) || 0;
  return (
    <div className="flex items-center gap-2">
      <Button type="button" variant="outline" size="icon" className="h-11 w-11" onClick={() => onChange(Math.max(0, n - 1))} data-testid={`${testId}-minus`}><Minus className="w-4 h-4" /></Button>
      <Input type="number" min="0" inputMode="numeric" value={value} onChange={(e) => onChange(e.target.value)} className="h-11 text-center text-lg font-bold" data-testid={testId} />
      <Button type="button" variant="outline" size="icon" className="h-11 w-11" onClick={() => onChange(n + 1)} data-testid={`${testId}-plus`}><Plus className="w-4 h-4" /></Button>
    </div>
  );
}

/** Blind count entry for one stock-take line (system quantity is never shown). */
export default function StocktakeCountSheet({ item, onClose, onSave, saving }) {
  const [count, setCount] = useState('');
  const [batchCounts, setBatchCounts] = useState({});

  useEffect(() => {
    if (!item) return;
    setCount(item.counted_quantity ?? '');
    setBatchCounts(Object.fromEntries((item.batches || []).map((b) => [b.batch_number, b.counted_quantity ?? ''])));
  }, [item]);

  if (!item) return null;
  const batches = item.batches || [];
  const total = batches.length ? Object.values(batchCounts).reduce((s, v) => s + (parseInt(v, 10) || 0), 0) : parseInt(count, 10) || 0;
  const save = () => onSave(item, total, batches.length
    ? Object.entries(batchCounts).map(([batch_number, v]) => ({ batch_number, counted_quantity: parseInt(v, 10) || 0 })) : null);

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="bottom" className="max-h-[88vh] overflow-y-auto rounded-t-2xl" data-testid="staff-app-count-sheet">
        <SheetHeader className="text-left">
          <SheetTitle>{item.product_name}</SheetTitle>
          <SheetDescription>{[item.category, item.bin_location && `Bin ${item.bin_location}`].filter(Boolean).join(' · ') || 'Count what is on the shelf'}</SheetDescription>
        </SheetHeader>
        <div className="space-y-3 py-4">
          {batches.length ? batches.map((b) => (
            <div key={b.batch_number} className="space-y-1">
              <p className="text-xs font-semibold">Batch {b.batch_number}{b.expiry_date ? ` · exp ${b.expiry_date.slice(0, 10)}` : ''}</p>
              <Stepper value={batchCounts[b.batch_number] ?? ''} onChange={(v) => setBatchCounts({ ...batchCounts, [b.batch_number]: v })} testId={`staff-app-count-batch-${b.batch_number}`} />
            </div>
          )) : <Stepper value={count} onChange={setCount} testId="staff-app-count-input" />}
          {batches.length > 0 && <p className="text-sm font-semibold" data-testid="staff-app-count-total">Total counted: {total}</p>}
        </div>
        <Button onClick={save} disabled={saving || (!batches.length && count === '')} className="w-full h-11" data-testid="staff-app-count-save">
          {saving ? 'Saving…' : 'Save count'}
        </Button>
      </SheetContent>
    </Sheet>
  );
}
